import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Building2, MapPin, Car,
    ArrowRight, ArrowLeft, Loader2, LogOut, CheckCircle2,
} from 'lucide-react';
import AccountActivationNotice from './AccountActivationNotice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    selectUser,
    logoutUser,
    logout,
    updateOrganisationStatus,
    setUserData,
} from '../store/slices/authSlice';
import {
    updateOrgDetails,
    selectOrgDetails,
    selectIsSubmitting,
    selectRegistrationError,
    clearRegistrationError,
    provisionOrganisation,
} from '../store/slices/registrationSlice';

const AUSTRALIAN_STATES = [
    { value: '', label: 'Select state' },
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'SA', label: 'South Australia' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'NT', label: 'Northern Territory' },
    { value: 'ACT', label: 'Australian Capital Territory' },
];

const ORGANISATION_TYPES = [
    { value: 'MECHANIC_WORKSHOP', label: 'Mechanic Workshop' },
    { value: 'CAR_WASH', label: 'Car Wash & Clean' },
];

const OrganisationSetup = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const user = useAppSelector(selectUser);
    const orgDetails = useAppSelector(selectOrgDetails);
    const isSubmitting = useAppSelector(selectIsSubmitting);
    const error = useAppSelector(selectRegistrationError);

    const [localErrors, setLocalErrors] = React.useState({});
    const [provisioned, setProvisioned] = React.useState(false);

    useEffect(() => {
        return () => { dispatch(clearRegistrationError()); };
    }, [dispatch]);

    const handleLogout = () => {
        dispatch(logoutUser());
        dispatch(logout());
    };

    const handleChange = (field) => (e) => {
        let value = e.target.value;

        // Postcode - only digits
        if (field === 'orgPostcode' && value && !/^\d*$/.test(value)) {
            return;
        }

        dispatch(updateOrgDetails({ [field]: value }));
        setLocalErrors(prev => ({ ...prev, [field]: null }));
        if (error) dispatch(clearRegistrationError());
    };

    const validate = () => {
        const errors = {};

        if (!orgDetails.organisationName?.trim()) errors.organisationName = 'Business name is required';

        setLocalErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const result = await dispatch(provisionOrganisation({
            ...orgDetails,
            organisationType: orgDetails.organisationType || 'MECHANIC_WORKSHOP',
        }));
        if (provisionOrganisation.fulfilled.match(result)) {
            dispatch(updateOrganisationStatus(true));

            // The thunk swaps in a token that carries the new organisation, so in the
            // normal case there is nothing left for the user to do — go straight into
            // the app. setUserData pushes the freshly parsed claims into Redux so the
            // sidebar renders from the NEW uiViews immediately rather than the empty
            // set the old token carried.
            if (result.payload?.sessionRefreshed && result.payload.user) {
                dispatch(setUserData(result.payload.user));
                navigate('/', { replace: true });
                return;
            }

            // Refresh failed — but the workshop WAS created, so this is not an error
            // state. Fall back to the manual sign-out, which always works because a
            // fresh login rebuilds the claims from scratch.
            setProvisioned(true);
        }
    };

    const inputBaseClass = "w-full py-3 bg-white border rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400";
    const inputWithIconClass = `${inputBaseClass} pl-11 pr-4`;
    const inputNoIconClass = `${inputBaseClass} pl-4 pr-4`;

    const getInputBorderClass = (field) =>
        localErrors[field] ? 'border-red-300' : 'border-gray-200';

    // After a successful setup, ask the user to re-authenticate so their next
    // session carries the full organisation claims/permissions.
    if (provisioned) {
        return (
            <div className="min-h-screen bg-gray-50">
                {/* Top bar */}
                <div className="bg-white border-b border-gray-100">
                    <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center">
                                <span className="text-white font-display font-bold text-base">W</span>
                            </div>
                            <span className="text-gray-900 font-display font-bold text-xl tracking-tight">wadakaroo</span>
                        </div>
                        <span className="text-sm text-gray-500 hidden sm:block">{user?.email}</span>
                    </div>
                </div>

                <div className="max-w-md mx-auto px-6 py-16">
                    <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                        <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
                            <CheckCircle2 className="text-emerald-600" size={28} />
                        </div>
                        <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
                            You're all set
                        </h2>
                        <p className="text-gray-500 mb-5">
                            Your business has been created. Please sign out and sign back in to
                            finish activating your account.
                        </p>

                        {/* Being told to sign out straight after finishing setup reads like
                            a failure unless the screen says it is the final step. */}
                        <AccountActivationNotice className="mb-6" />

                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-semibold text-sm bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors"
                        >
                            <LogOut size={16} />
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top bar */}
            <div className="bg-white border-b border-gray-100">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center">
                            <span className="text-white font-display font-bold text-base">W</span>
                        </div>
                        <span className="text-gray-900 font-display font-bold text-xl tracking-tight">wadakaroo</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500 hidden sm:block">{user?.email}</span>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer"
                        >
                            <LogOut size={16} />
                            <span className="hidden sm:inline">Sign out</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-6 py-10">
                {/* Step indicator */}
                <div className="flex items-center gap-2 mb-8">
                    <div className="flex items-center gap-1.5">
                        <div className="w-8 h-1.5 bg-emerald-500 rounded-full" />
                        <div className="w-8 h-1.5 bg-emerald-500 rounded-full" />
                        <div className="w-8 h-1.5 bg-emerald-500 rounded-full" />
                    </div>
                    <span className="text-xs text-gray-400 ml-2">Step 3 of 3</span>
                </div>

                <div className="mb-8">
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
                        Set up your business
                    </h2>
                    <p className="text-gray-500">
                        Add your business details to get started
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                            <p className="text-red-700 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Business Name Card */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                        <div className="flex items-center gap-2 mb-5">
                            <Building2 size={18} className="text-emerald-600" />
                            <h3 className="font-semibold text-gray-900">Business details</h3>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Business name <span className="text-red-400">*</span>
                            </label>
                            <div className="relative">
                                <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    value={orgDetails.organisationName}
                                    onChange={handleChange('organisationName')}
                                    className={`${inputWithIconClass} ${getInputBorderClass('organisationName')}`}
                                    placeholder="Acme Pty Ltd"
                                    required
                                />
                            </div>
                            {localErrors.organisationName && (
                                <p className="text-red-500 text-xs mt-1">{localErrors.organisationName}</p>
                            )}
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Business type <span className="text-red-400">*</span>
                            </label>
                            <div className="relative">
                                <Car className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <select
                                    value={orgDetails.organisationType || 'MECHANIC_WORKSHOP'}
                                    onChange={handleChange('organisationType')}
                                    className={`${inputWithIconClass} border-gray-200 appearance-none cursor-pointer`}
                                >
                                    {ORGANISATION_TYPES.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Business Address Card */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
                        <div className="flex items-center gap-2 mb-5">
                            <MapPin size={18} className="text-emerald-600" />
                            <h3 className="font-semibold text-gray-900">Business address</h3>
                            <span className="text-xs text-gray-400">(optional)</span>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Street address
                                </label>
                                <input
                                    type="text"
                                    value={orgDetails.orgAddress}
                                    onChange={handleChange('orgAddress')}
                                    className={`${inputNoIconClass} border-gray-200`}
                                    placeholder="123 Business Street"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        City / Suburb
                                    </label>
                                    <input
                                        type="text"
                                        value={orgDetails.orgCity}
                                        onChange={handleChange('orgCity')}
                                        className={`${inputNoIconClass} border-gray-200`}
                                        placeholder="Melbourne"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        State
                                    </label>
                                    <select
                                        value={orgDetails.orgState}
                                        onChange={handleChange('orgState')}
                                        className={`${inputNoIconClass} border-gray-200 appearance-none cursor-pointer`}
                                    >
                                        {AUSTRALIAN_STATES.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Postcode
                                    </label>
                                    <input
                                        type="text"
                                        value={orgDetails.orgPostcode}
                                        onChange={handleChange('orgPostcode')}
                                        className={`${inputNoIconClass} border-gray-200`}
                                        placeholder="3000"
                                        maxLength={4}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                        <button
                            type="button"
                            onClick={() => navigate('/complete-profile')}
                            className="flex items-center gap-2 px-5 py-3 text-sm font-medium text-gray-600 hover:text-gray-800 bg-transparent border-none cursor-pointer"
                        >
                            <ArrowLeft size={16} />
                            Back
                        </button>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-sm transition-all ${
                                isSubmitting
                                    ? 'bg-emerald-300 cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]'
                            } text-white shadow-sm`}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} />
                                    Setting up...
                                </>
                            ) : (
                                <>
                                    Complete
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OrganisationSetup;